import PropTypes from 'prop-types';
import {
    Button,
    Divider,
    FormControl,
    Grid2 as Grid,
    InputLabel,
    MenuItem,
    TextField,
    Typography
} from '@mui/material';
import i18next, { t } from 'i18next';
import { useMemo } from 'react';
import { HeartRateConfiguration } from '../../Users/infoUser/HeartRateConfiguration';
import HealthHabitsSection from '../../Users/infoUser/HealthHabitsSection';
import { CommonSelect } from 'component/select';
import { CommonCard } from 'component/cards/MainCard2';

function MedicalBodyCard({ formik }) {
    const trainingBackgroundOptions = [
        { value: 10, label: t('users.trainingBackground.occasional') },
        { value: 20, label: t('users.trainingBackground.regular') },
        { value: 30, label: t('users.trainingBackground.frequent') },
        { value: 40, label: t('users.trainingBackground.heavy') },
        { value: 50, label: t('users.trainingBackground.semiPro') },
        { value: 60, label: t('users.trainingBackground.pro') }
    ];

    const typicalDayOptions = [
        { value: 1, label: t('users.typicalDay.mostlyMoving') },
        { value: 2, label: t('users.typicalDay.mostlySitting') },
        { value: 3, label: t('users.typicalDay.mostlyStanding') }
    ];

    const estimatedVo2Max = useMemo(() => {
        const maxHr = Number(formik.values.maxHeartRate);
        const restingHr = Number(formik.values.restingHeartRate);

        if (!maxHr || !restingHr || restingHr <= 0) return null;

        const estimate = 15.3 * (maxHr / restingHr);
        return Number.isFinite(estimate) ? Number(estimate.toFixed(1)) : null;
    }, [formik.values.maxHeartRate, formik.values.restingHeartRate]);
    const isEnglish = i18next.resolvedLanguage?.startsWith('en');
    const estimateLabel = isEnglish ? 'Estimated' : 'Estimativa';
    const useEstimateLabel = isEnglish ? 'Use estimate' : 'Usar estimativa';

    return (
        <CommonCard sx={{ p: 2 }}>
            <Grid container spacing={2} sx={{ width: '100%' }}>
                <Grid size={12}>
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <Typography variant="subtitle1">
                                {t('users.sections.heartRateThresholds')}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 8, lg: 7 }}>
                            <HeartRateConfiguration formik={formik} />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid size={12}>
                    <Divider />
                </Grid>

                <Grid size={12}>
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <Typography variant="subtitle1">
                                {t('users.sections.wearableConfig')}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                label={t('users.fields.height')}
                                type="number"
                                name="height"
                                value={formik.values.height}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                label={t('users.fields.weight')}
                                type="number"
                                name="weight"
                                value={formik.values.weight}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                label={t('users.fields.vo2Max')}
                                type="number"
                                name="vo2Max"
                                value={formik.values.vo2Max}
                                onChange={formik.handleChange}
                                placeholder={
                                    estimatedVo2Max ? String(estimatedVo2Max) : undefined
                                }
                                helperText={
                                    estimatedVo2Max
                                        ? `${estimateLabel}: ${estimatedVo2Max}`
                                        : ''
                                }
                            />
                            {estimatedVo2Max && (
                                <Button
                                    size="small"
                                    sx={{ mt: 0.5 }}
                                    onClick={() =>
                                        formik.setFieldValue('vo2Max', estimatedVo2Max)
                                    }
                                >
                                    {useEstimateLabel}
                                </Button>
                            )}
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel id="training-background-label">
                                    {t('users.fields.trainingBackground')}
                                </InputLabel>
                                <CommonSelect
                                    labelId="training-background-label"
                                    name="trainingBackgorund"
                                    label={t('users.fields.trainingBackground')}
                                    value={formik.values.trainingBackgorund}
                                    onChange={formik.handleChange}
                                >
                                    {trainingBackgroundOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </CommonSelect>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel id="typical-day-label">
                                    {t('users.fields.typicalDay')}
                                </InputLabel>
                                <CommonSelect
                                    labelId="typical-day-label"
                                    name="typicalDay"
                                    label={t('users.fields.typicalDay')}
                                    value={formik.values.typicalDay}
                                    onChange={formik.handleChange}
                                >
                                    {typicalDayOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </CommonSelect>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                label={t('users.fields.sleepGoalMinutes')}
                                type="number"
                                name="sleepGoalMinutes"
                                value={formik.values.sleepGoalMinutes}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid size={12}>
                    <Divider />
                </Grid>
                <Grid size={12}>
                    <HealthHabitsSection formik={formik} headingVariant="subtitle1" />
                </Grid>
            </Grid>
        </CommonCard>
    );
}

MedicalBodyCard.propTypes = {
    formik: PropTypes.object.isRequired
};

export default MedicalBodyCard;
